import React from 'react';
import {
  buildTextDiffLines,
  getFieldDiffDisplayLines,
  type FieldDiffDisplayMode,
  type TextDiffLine,
  usesWordLevelDiff,
} from '../utils/textFieldDiff';

export interface NltTextFieldDiffLinesProps {
  oldValue: string;
  newValue: string;
  mode?: FieldDiffDisplayMode;
  variant?: 'configurator' | 'summary';
  mutedRemoved?: boolean;
}


function renderInlineConfiguratorParts(parts: TextDiffLine[], mutedRemoved: boolean) {
  return (
    <div className="ac-field-patch__line ac-field-patch__line--inline">
      <span className="ac-field-patch__gutter"> </span>
      <div className="ac-field-patch__inline-text">
        {parts.map((part, index) => {
          if (part.kind === 'same') {
            return (
              <React.Fragment key={`same-${index}`}>
                {index > 0 ? ' ' : ''}
                <span className="ac-field-patch__inline-part ac-field-patch__inline-part--same">
                  {part.text}
                </span>
              </React.Fragment>
            );
          }
          const isRemoved = part.kind === 'removed';
          return (
            <React.Fragment key={`${part.kind}-${index}`}>
              {index > 0 ? ' ' : ''}
              <span
                className={[
                  'ac-field-patch__inline-part',
                  isRemoved ? 'ac-field-patch__inline-part--removed' : 'ac-field-patch__inline-part--added',
                  isRemoved && mutedRemoved ? 'ac-field-patch__inline-part--muted' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {part.text}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function renderInlineSummaryParts(parts: TextDiffLine[]) {
  const changed = parts.filter((part) => part.kind !== 'same');
  if (changed.length === 0) return null;

  return (
    <div className="nlt-patch-summary__preview-diff nlt-patch-summary__preview-diff--inline">
      {changed.map((part, index) => (
        <span
          key={`${part.kind}-${index}`}
          className={
            part.kind === 'added'
              ? 'nlt-patch-summary__preview-new nlt-patch-summary__preview-inline-part'
              : 'nlt-patch-summary__preview-old nlt-patch-summary__preview-inline-part'
          }
        >
          {index > 0 ? ' ' : ''}
          {part.kind === 'added' ? '+ ' : '− '}
          {part.text}
        </span>
      ))}
    </div>
  );
}

const NltTextFieldDiffLines: React.FC<NltTextFieldDiffLinesProps> = ({
  oldValue,
  newValue,
  mode = 'pending',
  variant = 'configurator',
  mutedRemoved = false,
}) => {
  const useInlineWordDiff = usesWordLevelDiff(oldValue, newValue) && mode === 'pending';
  const lines = useInlineWordDiff
    ? buildTextDiffLines(oldValue, newValue)
    : getFieldDiffDisplayLines(oldValue, newValue, mode);
  if (lines.length === 0) return null;

  if (useInlineWordDiff) {
    if (variant === 'summary') {
      return renderInlineSummaryParts(lines);
    }
    return <>{renderInlineConfiguratorParts(lines, mutedRemoved)}</>;
  }

  if (variant === 'summary') {
    return (
      <div className="nlt-patch-summary__preview-diff">
        {lines.map((line, index) => (
          <div
            key={`${line.kind}-${index}`}
            className={
              line.kind === 'added'
                ? 'nlt-patch-summary__preview-new'
                : 'nlt-patch-summary__preview-old'
            }
          >
            {line.kind === 'added' ? '+ ' : '− '}
            {line.text}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {lines.map((line, index) => {
        const isRemoved = line.kind === 'removed';
        const isAdded = line.kind === 'added';
        return (
          <div
            key={`${line.kind}-${index}`}
            className={[
              'ac-field-patch__line',
              isRemoved ? 'ac-field-patch__line--removed' : '',
              isAdded ? 'ac-field-patch__line--added' : '',
              isRemoved && mutedRemoved ? 'ac-field-patch__line--muted' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="ac-field-patch__gutter">{isAdded ? '+' : isRemoved ? '−' : ' '}</span>
            <pre className="ac-field-patch__text">{line.text || ' '}</pre>
          </div>
        );
      })}
    </>
  );
};

export default NltTextFieldDiffLines;
